"use client"

import { useEffect, useRef } from "react"
import { motion, AnimatePresence } from "motion/react"
import { LoginForm } from "./login-form"
import { X } from "lucide-react"
import { useDraggableDrawer } from "@/shared/hooks/use-draggable-drawer"
import { useMediaQuery } from "@/shared/hooks/use-media-query"

type Props = {
    isOpen: boolean
    onClose?: () => void
    onSuccess?: () => void
    allowClose?: boolean
}

// Visual configuration matching the requested "fade into transparency" style
// Now using CSS variables for theme support
const DRAWER_STYLES = {
    // The gradient that fades from solid background to transparent
    // We use a CSS variable for the color so it adapts to light/dark mode
    // We need to double-layer it to ensure opacity at the bottom
    background: `linear-gradient(to top, var(--background) 0%, var(--background) 80%, transparent 100%)`,
}

const styles = {
    overlay: {
        position: "fixed" as const,
        inset: 0,
        zIndex: 50,
        display: "flex",
        flexDirection: "column" as const,
        justifyContent: "flex-end", // Align content to bottom
        alignItems: "center",
    },
    // The blur backdrop purely for visual separation (dim + blur)
    backdrop: {
        position: "absolute" as const,
        inset: 0,
        // Using CSS variable for color to adapt to theme (dark vs light tint)
        backgroundColor: "var(--overlay-backdrop, rgba(0, 0, 0, 0.2))",
        backdropFilter: "blur(3px)", // Reduced blur to let shapes/colors be visible
        WebkitBackdropFilter: "blur(3px)",
        // Performance optimizations
        willChange: "backdrop-filter",
        contain: "paint",
    },
    // The drawer container with the gradient fade
    drawer: {
        position: "relative" as const,
        zIndex: 10,
        width: "100%",
        maxWidth: "100%",
        minHeight: "70vh", // Use reasonable height coverage
        display: "flex",
        flexDirection: "column" as const,
        justifyContent: "flex-end",
        alignItems: "center",
        paddingBottom: "2rem",
        background: DRAWER_STYLES.background,
        // Remove strict borders/shadows to let the gradient do the work
        border: "none",
        boxShadow: "none",
        pointerEvents: "none" as const, // Let clicks pass through transparent/empty parts
    },
    // Drag handle for mobile
    dragHandle: {
        position: "sticky" as const,
        top: 0,
        width: "100%",
        zIndex: 3,
        paddingTop: "0.75rem",
        paddingBottom: "0.5rem",
        display: "flex",
        justifyContent: "center",
        pointerEvents: "auto" as const,
        cursor: "grab",
    },
    dragHandleBar: {
        width: "2.5rem",
        height: "0.25rem",
        borderRadius: "9999px",
        backgroundColor: "var(--muted-foreground)",
        opacity: 0.3,
    },
    contentWrapper: {
        width: "100%",
        maxWidth: "28rem",
        position: "relative" as const,
        // Ensure form sits in the solid part of the gradient
        backgroundColor: "transparent",
        padding: "1rem",
        pointerEvents: "auto" as const, // Re-enable clicks for content
    },
    closeButton: {
        position: "absolute" as const,
        top: "-3rem", // Float above the form
        right: "0",
        padding: "0.5rem",
        borderRadius: "0.5rem",
        backgroundColor: "var(--muted)",
        border: "1px solid var(--border)",
        cursor: "pointer",
        color: "var(--muted-foreground)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(4px)",
    },
}

export function AuthOverlay({ isOpen, onClose, onSuccess, allowClose = true }: Props) {
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const isMobile = useMediaQuery("(max-width: 768px)")

    // Draggable drawer hook for mobile
    const {
        y,
        isDragging,
        onDrag,
        onDragEnd,
        dragConstraints,
        dragElastic
    } = useDraggableDrawer({
        onClose: () => {
            if (allowClose && onClose) {
                onClose()
            }
        },
        closeThreshold: 0.15,
        velocityThreshold: 500,
        enabled: isMobile && allowClose
    })

    useEffect(() => {
        if (isOpen && closeButtonRef.current && allowClose) {
            closeButtonRef.current.focus()
        }
    }, [isOpen, allowClose])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen && allowClose && onClose) {
                onClose()
            }
        }
        document.addEventListener("keydown", handleEscape)
        return () => document.removeEventListener("keydown", handleEscape)
    }, [isOpen, allowClose, onClose])

    useEffect(() => {
        // Prevent body scroll when locked
        if (isOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = ""
        }
        return () => {
            document.body.style.overflow = ""
        }
    }, [isOpen])

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={styles.overlay}
                >
                    {/* Blurred backdrop layer */}
                    <div
                        style={styles.backdrop}
                        onClick={allowClose && onClose ? onClose : undefined}
                    />

                    {/* Gradient Drawer */}
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={isMobile ? { y: 0 } : { y: 0 }}
                        exit={{ y: "100%" }}
                        transition={isDragging ? { duration: 0 } : { duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                        drag={isMobile && allowClose ? "y" : false}
                        dragConstraints={dragConstraints}
                        dragElastic={dragElastic}
                        onDrag={onDrag}
                        onDragEnd={onDragEnd}
                        style={{
                            ...styles.drawer,
                            y: isMobile ? y : 0,
                            cursor: isMobile ? (isDragging ? "grabbing" : "grab") : "default"
                        }}
                    >
                        {/* Mobile drag handle */}
                        {isMobile && allowClose && (
                            <div style={styles.dragHandle}>
                                <div style={styles.dragHandleBar} aria-label="Drag to close" />
                            </div>
                        )}

                        <div style={styles.contentWrapper}>
                            {allowClose && onClose && (
                                <button
                                    ref={closeButtonRef}
                                    onClick={onClose}
                                    style={styles.closeButton}
                                    aria-label="Close"
                                >
                                    <X style={{ width: 20, height: 20 }} />
                                </button>
                            )}

                            {/* Pass onSuccess to form to handle completion */}
                            <LoginForm
                                onSuccess={() => {
                                    onSuccess?.()
                                    onClose?.()
                                }}
                            />
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

